import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  getTypographyFieldsForWidget,
  getMobileTypographyOverrides,
  hasMobileTypographyOverrides,
  clearMobileTypographyOverrides,
  patchResponsiveMobileTypography,
  supportsMobileTypography,
} from '../utils/responsiveTypography';
import { BREAKPOINT_MOBILE_MAX } from '../constants/responsiveBreakpoints';

const SliderControl = ({ label, value, onChange, min = 10, max = 60 }) => (
  <div className="bc-slider-row">
    <div className="bc-slider-label">{label}</div>
    <input type="range" className="bc-slider" min={min} max={max} value={value} onChange={(e) => onChange(e.target.value)} />
    <input type="number" className="bc-slider-value" value={value} onChange={(e) => onChange(e.target.value)} min={min} max={max} />
  </div>
);

/**
 * Overrides de tipografia mobile por widget — Fase 2.
 */
export default function TypographyMobileOverridesPanel({ widgetType, content, onContentChange, editBreakpoint, courseGlobalConfig = {}, embedded = false }) {
  const { t } = useTranslation('builder');
  const fields = getTypographyFieldsForWidget(widgetType);

  if (!supportsMobileTypography(widgetType) || !fields?.length) return null;

  const mobile = getMobileTypographyOverrides(content);
  const customized = hasMobileTypographyOverrides(content, widgetType);
  const isEditingMobile = editBreakpoint === 'mobile';

  const apply = (next) => onContentChange(next);
  const updateTypo = (key, val) => apply(patchResponsiveMobileTypography(content, key, val));

  const desktopHint = (field) => {
    if (widgetType === 'global') {
      const d = parseInt(courseGlobalConfig[field.key], 10);
      if (Number.isFinite(d) && d > 0) return `${d}px`;
      if (field.defaultDesktop) return `${field.defaultDesktop}px`;
    }
    const d = parseInt(content[field.key], 10);
    if (Number.isFinite(d) && d > 0) return `${d}px`;
    return t('builder:mobileTypoInheritDesktop', { defaultValue: 'herda desktop' });
  };

  return (
    <div
      style={embedded ? undefined : {
        marginBottom: '1rem',
        paddingBottom: '1rem',
        borderBottom: '1px solid #e2e8f0',
        background: isEditingMobile ? '#eff6ff' : customized ? '#f0fdf4' : 'transparent',
        borderRadius: isEditingMobile || customized ? '8px' : 0,
        padding: isEditingMobile || customized ? '12px' : 0,
      }}
    >
      {(!embedded || customized) && (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '10px' }}>
        {!embedded && (
          <span className="bc-label" style={{ margin: 0 }}>
            {t('builder:mobileTypoSectionTitle', { defaultValue: 'Fontes mobile' })} (≤{BREAKPOINT_MOBILE_MAX}px)
          </span>
        )}
        {customized ? (
          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#15803d', background: '#dcfce7', padding: '2px 8px', borderRadius: 999 }}>
            {t('builder:mobileLayoutCustomized', { defaultValue: 'Personalizado' })}
          </span>
        ) : null}
      </div>
      )}
      {isEditingMobile ? (
        <p className="bc-hint" style={{ marginTop: 0, marginBottom: '10px' }}>
          {t('builder:mobileTypoEditingHint', { defaultValue: 'Valores aplicados em telas pequenas. Deixe no mínimo para herdar o desktop.' })}
        </p>
      ) : (
        <p className="bc-hint" style={{ marginTop: 0, marginBottom: '10px' }}>
          {t('builder:mobileTypoHint', { defaultValue: 'Opcional. Sobrescreve tamanhos de fonte só no mobile.' })}
        </p>
      )}

      {fields.map((field) => {
        const stored = parseInt(mobile[field.key], 10);
        const desktopPx = parseInt(content[field.key], 10);
        const desktopVal = (Number.isFinite(desktopPx) && desktopPx > 0)
          ? desktopPx
          : (widgetType === 'global' && field.defaultDesktop ? field.defaultDesktop : field.min);
        const sliderVal = Number.isFinite(stored) && stored > 0 ? stored : desktopVal;
        const label = t(`builder:${field.labelKey}`, { defaultValue: field.key });
        return (
          <div key={field.key} className="bc-form-group" style={{ marginBottom: '8px' }}>
            <SliderControl
              label={`${label} (${t('builder:mobileTypoDesktopRef', { defaultValue: 'desktop' })}: ${desktopHint(field)})`}
              value={sliderVal}
              onChange={(v) => updateTypo(field.key, v)}
              min={field.min}
              max={field.max}
            />
          </div>
        );
      })}

      {customized ? (
        <button
          type="button"
          className="bc-btn-cancel"
          style={{ marginTop: '10px', fontSize: '0.8rem', padding: '6px 12px' }}
          onClick={() => apply(clearMobileTypographyOverrides(content))}
        >
          {t('builder:mobileTypoClear', { defaultValue: 'Limpar fontes mobile' })}
        </button>
      ) : null}
    </div>
  );
}
